import {
  teamInvitesQueryOptions,
  teamMembersQueryOptions,
  teamsQueryOptions,
  UpdateTeamDto,
  useTeamUpdateMutation,
} from '@blesk-frontend/api';
import {useBleskAuth, useTeamSubscription} from '@blesk-frontend/auth';
import {Button, FieldGroup, Input, Page, PageTitle, useModal} from '@blesk-frontend/ui';
import {useQuery} from '@tanstack/react-query';
import {useEffect, useMemo} from 'react';
import {useForm} from 'react-hook-form';
import {toast} from 'sonner';

import {GuideListTopbar} from '@/features/guides';
import {PlanPaywallModal} from '@/features/plans';
import {Route} from '../../../routes/_app/team';
import {CreateTeamInviteModal, TeamMemberInviteHint, TeamMembersTable} from '../components';

export const TeamPage = () => {
  const title = Route.useRouteContext().getTitle();
  const {
    query: {data: user, refetch},
    teamId,
  } = useBleskAuth();

  const {data: teams} = useQuery(teamsQueryOptions());
  const {mutateAsync: updateTeam} = useTeamUpdateMutation();

  const currentTeam = useMemo(() => {
    return teams?.find((team) => team.id === teamId);
  }, [teams, teamId]);

  const {subscription} = useTeamSubscription();
  const {data: teamMembers} = useQuery(teamMembersQueryOptions(teamId || ''));
  const {data: teamInvites} = useQuery(teamInvitesQueryOptions(teamId || ''));

  const isOwner = useMemo(() => {
    return (
      currentTeam?.members.some(
        (member) => member.userId === user?.id && member.role === 'owner',
      ) || false
    );
  }, [currentTeam, user]);

  const allowInvite = useMemo(() => {
    if (!isOwner || !teamMembers || !teamInvites) {
      return false;
    }
    const isTeamPlan = subscription?.plan?.name.toLowerCase().includes('team');

    return isOwner && (isTeamPlan || teamMembers.length + teamInvites.length < 2);
  }, [isOwner, teamMembers, teamInvites, subscription]);

  const {
    formState: {isSubmitting},
    register,
    handleSubmit,
    setValue,
  } = useForm<UpdateTeamDto>();

  useEffect(() => {
    if (currentTeam) {
      setValue('name', currentTeam.name);
    }
  }, [currentTeam, setValue]);

  const onSubmit = handleSubmit(async ({name}) => {
    return toast.promise(
      async () => {
        await updateTeam({id: currentTeam?.id as string, name});
        await refetch();
      },
      {
        loading: 'Сохранение...',
        error: 'При сохранении произошла ошибка',
        success: () => {
          refetch();
          return 'Название успешно сохранено';
        },
      },
    );
  });
  const openCreateTeamInviteModal = useModal((props) => <CreateTeamInviteModal {...props} />);
  const openPaywallModal = useModal((props) => <PlanPaywallModal {...props} />);

  return (
    <Page className="flex h-full flex-col">
      <GuideListTopbar />

      <div className="container mx-auto flex h-full max-w-7xl flex-col gap-8">
        <PageTitle>
          <div className="flex h-10 items-center justify-center">
            <span>{title}</span>
          </div>
        </PageTitle>

        {allowInvite && <TeamMemberInviteHint />}

        <form className="flex max-w-lg items-end gap-4" onSubmit={onSubmit}>
          <FieldGroup
            label="Название команды"
            labelFor="name"
            className="flex w-full flex-col gap-1.5"
          >
            <Input {...register('name')} id="name" type="text" disabled={!isOwner} />
          </FieldGroup>

          {isOwner && (
            <Button className="w-min px-6" type="submit" loading={isSubmitting}>
              <span>Сохранить</span>
            </Button>
          )}
        </form>

        <TeamMembersTable
          teamMembers={teamMembers}
          teamInvites={teamInvites}
          teamId={currentTeam?.id as string}
          isOwner={isOwner}
        />

        {isOwner && (
          <Button
            className="w-fit px-6"
            onClick={() => (allowInvite ? openCreateTeamInviteModal() : openPaywallModal())}
          >
            Пригласить коллегу
          </Button>
        )}
      </div>
    </Page>
  );
};
